<template>
  <div class="cms-pages">
    <cms-page-header
      :title="$t('cms.pages.title')"
      :create-text="$t('cms.pages.createNew')"
      @create="navigateToCreate"
    />

    <cms-filter-bar>
      <cms-filter-group :label="$t('cms.pages.status')">
        <select v-model="selectedStatus" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option v-for="(label, key) in statusOptions" :key="key" :value="key">
            {{ label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('cms.pages.pageType')">
        <select v-model="selectedPageType" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option v-for="(label, key) in pageTypeOptions" :key="key" :value="key">
            {{ label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('general.search')">
        <input
          v-model="searchQuery"
          type="text"
          class="cms-input"
          :placeholder="$t('cms.pages.searchPlaceholder')"
        />
      </cms-filter-group>

      <cms-filter-group :label="''">
        <div class="cms-toggle-group">
          <label class="cms-checkbox-item">
            <input v-model="onlyPublic" type="checkbox" class="cms-checkbox" />
            <span>{{ $t('cms.pages.filters.public') }}</span>
          </label>
        </div>
      </cms-filter-group>

      <cms-filter-group :label="''">
        <button class="cms-btn cms-btn-secondary" type="button" :disabled="isLoading" @click="loadPages">
          <v-icon icon="refresh" size="sm" />
          <span>{{ $t('general.refresh') }}</span>
        </button>
      </cms-filter-group>
    </cms-filter-bar>

    <div v-if="errorMessage" class="cms-pages-error">
      <p>{{ errorMessage }}</p>
      <button type="button" class="cms-btn-link" @click="loadPages">{{ $t('general.retry') }}</button>
    </div>

    <div v-if="isLoading" class="cms-pages-loading">
      <div class="cms-loading-spinner" />
      <p>{{ $t('general.loading') }}</p>
    </div>

    <cms-empty-state
      v-else-if="filteredPages.length === 0"
      :message="$t('cms.pages.list.emptyStateTitle')"
      :hint="$t('cms.pages.list.emptyStateHint')"
    />

    <cms-grid v-else min-width="320px" gap="lg">
      <div
        v-for="page in filteredPages"
        :key="page.id"
        class="cms-page-card-wrapper"
        @mouseover="hoveredPageId = page.id"
        @mouseout="hoveredPageId = null"
      >
        <div
          class="cms-page-card"
          @click="navigateToEdit(page.id)"
        >
          <!-- Image Section -->
          <div
            class="cms-page-image-section"
            :style="getImageStyle(page)"
          >
            <!-- Badges -->
            <div class="cms-page-badges">
              <span
                class="cms-page-status-badge"
                :class="page.status"
              >
                {{ statusOptions[page.status] || page.status }}
              </span>
              <span
                v-if="page.pageType"
                class="cms-page-type-badge"
              >
                {{ pageTypeOptions[page.pageType as keyof typeof pageTypeOptions] || page.pageType }}
              </span>
            </div>

            <!-- Overlay with actions on hover -->
            <div
              class="cms-page-overlay"
              :class="{ visible: hoveredPageId === page.id }"
            >
              <div class="cms-page-actions">
                <button
                  class="cms-page-action-btn edit-btn"
                  :title="$t('general.edit')"
                  @click.stop="navigateToEdit(page.id)"
                >
                  <v-icon icon="pencil" size="sm" />
                </button>
                <button
                  class="cms-page-action-btn publish-btn"
                  :title="page.isPublic ? $t('cms.pages.unpublishSuccess') : $t('cms.pages.publishSuccess')"
                  :disabled="publishingId === page.id"
                  @click.stop="togglePublish(page)"
                >
                  <v-icon :icon="page.isPublic ? 'eye-blocked' : 'eye'" size="sm" />
                </button>
                <button
                  class="cms-page-action-btn delete-btn"
                  :title="$t('general.delete')"
                  :disabled="deletingId === page.id"
                  @click.stop="deletePage(page)"
                >
                  <v-icon icon="trash" size="sm" />
                </button>
              </div>
            </div>

            <!-- No image placeholder -->
            <div v-if="!page.featuredImage" class="cms-page-no-image">
              <v-icon icon="image" size="lg" />
              <span class="cms-page-no-image-text">No Image</span>
            </div>
          </div>

          <!-- Content Section -->
          <div class="cms-page-content">
            <div class="cms-page-header">
              <h3 class="cms-page-title">{{ page.title }}</h3>
            </div>

            <div
              v-if="page.excerpt"
              class="cms-page-excerpt"
              v-html="truncateHtml(page.excerpt, 150)"
            />

            <div class="cms-page-meta">
              <div v-if="page.customUrl" class="cms-page-meta-item">
                <v-icon icon="link" size="xs" />
                <span>/{{ page.customUrl }}</span>
              </div>
              <div class="cms-page-meta-item">
                <v-icon icon="calendar" size="xs" />
                <span>{{ page.updatedAtLabel }}</span>
              </div>
            </div>

            <div class="cms-page-footer">
              <span class="cms-page-locale">
                {{ page.locale?.toUpperCase() || 'EN' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </cms-grid>

    <cms-pagination
      v-if="paginationMeta"
      :meta="paginationMeta"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import {
  CmsPageHeader,
  CmsFilterBar,
  CmsFilterGroup,
  CmsGrid,
  CmsEmptyState,
  CmsPagination,
} from '@/components/cms';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';

interface CmsPageCard {
  id: number;
  title: string;
  excerpt: string;
  customUrl: string;
  pageType: string;
  isPublic: boolean;
  updatedAt: string | null;
  updatedAtLabel: string;
  status: 'draft' | 'published';
  locale: string;
  featuredImage?: string;
}

export default defineComponent({
  name: 'TheCmsPages',
  components: {
    CmsPageHeader,
    CmsFilterBar,
    CmsFilterGroup,
    CmsGrid,
    CmsEmptyState,
    CmsPagination,
    VIcon,
  },
  setup() {
    const router = useRouter();
    const toast = useToast();
    const { t } = useI18n();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const { resolveStoredLocale } = useAppLanguages();
    const currentLocale = ref(resolveStoredLocale(localStorage.getItem('language')));

    const pages = ref<CmsPageCard[]>([]);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const publishingId = ref<number | null>(null);
    const deletingId = ref<number | null>(null);
    const hoveredPageId = ref<number | null>(null);

    // Pagination state
    const currentPage = ref(1);
    const perPage = ref(25);
    const paginationMeta = ref<{
      currentPage: number;
      lastPage: number;
      perPage: number;
      from: number | null;
      to: number | null;
      total: number;
    } | null>(null);

    const selectedStatus = ref('');
    const selectedPageType = ref('');
    const onlyPublic = ref(false);
    const searchQuery = ref('');

    const statusOptions = computed(() => ({
      draft: t('cms.pages.statusOptions.draft'),
      published: t('cms.pages.statusOptions.published'),
    }));

    const pageTypeOptions = computed(() => ({
      custom: t('cms.pages.pageTypeOptions.custom'),
      about: t('cms.pages.pageTypeOptions.about'),
      terms: t('cms.pages.pageTypeOptions.terms'),
      privacy: t('cms.pages.pageTypeOptions.privacy'),
    }));

    const filteredPages = computed(() => pages.value);

    const extractListData = (response: any): any[] => {
      if (!response) return [];
      const candidates = [
        response?.data?.data,
        response?.data,
        response?.data?.data?.data,
        response,
      ];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const formatDateTime = (value: string | null | undefined) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleString();
    };

    const computeStatus = (record: any): 'draft' | 'published' => {
      const isPublic = !!record.isPublic;
      return isPublic ? 'published' : 'draft';
    };

    const extractTranslations = (page: any) => {
      const relationship = extractRelationship(page, 'translations');
      const normalizedRelationship = normalizeResource(relationship);
      if (Array.isArray(normalizedRelationship)) {
        return normalizedRelationship;
      }
      if (Array.isArray(page?.translations)) {
        return page.translations;
      }
      if (normalizedRelationship) {
        return [normalizedRelationship];
      }
      return [];
    };

    const extractMediaItems = (page: any) => {
      const relationship = extractRelationship(page, 'media');
      const normalized = normalizeResource(relationship);
      if (Array.isArray(normalized)) {
        return normalized;
      }
      if (Array.isArray(page?.media)) {
        return page.media;
      }
      return [];
    };

    const mapPageRecord = (item: any): CmsPageCard => {
      const normalized = normalizeResource(item) || item;
      const translations = extractTranslations(normalized);
      const translation =
        translations.find((tr: any) => (tr?.locale || '').toLowerCase() === currentLocale.value) ||
        translations[0] ||
        {};

      const mediaItems = extractMediaItems(normalized);
      const featuredMedia = mediaItems[0];
      
      const status = computeStatus(normalized);
      return {
        id: normalized.id,
        title: translation?.title || normalized.title || '-',
        excerpt: translation?.content ? translation.content.substring(0, 150) + '...' : '',
        customUrl: normalized.customUrl || '',
        pageType: normalized.pageType || 'custom',
        isPublic: !!normalized.isPublic,
        updatedAt: normalized.updatedAt || null,
        updatedAtLabel: formatDateTime(normalized.updatedAt),
        status,
        locale: (translation?.locale || currentLocale.value || 'en').toLowerCase(),
        featuredImage: featuredMedia?.originalUrl || '',
      };
    };

    const buildFilters = () => {
      const filters: Record<string, any> = {
        language: currentLocale.value,
      };

      if (selectedPageType.value) {
        filters.page_type = selectedPageType.value;
      }

      if (onlyPublic.value) {
        filters.is_public = 1;
      }

      if (searchQuery.value.trim()) {
        filters.search = searchQuery.value.trim();
      }

      return filters;
    };

    const loadPages = async () => {
      if (!agencySlug.value) {
        return;
      }

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const response = await api.pageService.listCmsPages(agencySlug.value, {
          page: currentPage.value,
          perPage: perPage.value,
          filters: buildFilters(),
          order: { key: 'updated_at', direction: 'desc' },
        });

        const list = extractListData(response);
        pages.value = list.map(mapPageRecord);

        // Extract pagination meta from response
        if (response?.meta) {
          paginationMeta.value = {
            currentPage: response.meta.current_page || currentPage.value,
            lastPage: response.meta.last_page || 1,
            perPage: response.meta.per_page || perPage.value,
            from: response.meta.from || null,
            to: response.meta.to || null,
            total: response.meta.total || 0,
          };
        } else {
          paginationMeta.value = null;
        }
      } catch (error: any) {
        console.error('Failed to load CMS pages', error);
        errorMessage.value = error?.response?.data?.message || t('general.errorMessage');
        toast.error(errorMessage.value);
      } finally {
        isLoading.value = false;
      }
    };

    const handlePageChange = (page: number) => {
      currentPage.value = page;
      loadPages();
    };

    const handlePerPageChange = (newPerPage: number) => {
      perPage.value = newPerPage;
      currentPage.value = 1;
      loadPages();
    };

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          currentPage.value = 1;
          loadPages();
        }
      },
      { immediate: true },
    );

    watch(
      [selectedPageType, onlyPublic],
      () => {
        if (!agencySlug.value) return;
        currentPage.value = 1;
        loadPages();
      },
    );

    let searchDebounce: ReturnType<typeof setTimeout> | null = null;
    watch(
      () => searchQuery.value,
      () => {
        if (!agencySlug.value) return;
        if (searchDebounce) {
          clearTimeout(searchDebounce);
        }
        searchDebounce = setTimeout(() => {
          currentPage.value = 1;
          loadPages();
        }, 400);
      },
    );

    const navigateToCreate = () => {
      router.push({ name: 'cms.pages.create' });
    };

    const navigateToEdit = (pageId: number) => {
      router.push({ name: 'cms.pages.edit', params: { id: pageId } });
    };

    const togglePublish = async (page: CmsPageCard) => {
      if (!agencySlug.value) return;

      const nextState = !page.isPublic;
      publishingId.value = page.id;

      try {
        await api.pageService.publishCmsPage(agencySlug.value, page.id, {
          is_public: nextState,
        });
        pages.value = pages.value.map((item) =>
          item.id === page.id
            ? {
                ...item,
                isPublic: nextState,
                status: nextState ? 'published' : 'draft',
              }
            : item,
        );
        toast.success(nextState ? t('cms.pages.publishSuccess') : t('cms.pages.unpublishSuccess'));
      } catch (error: any) {
        console.error('Failed to toggle publish state', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        publishingId.value = null;
      }
    };

    const deletePage = async (page: CmsPageCard) => {
      if (!agencySlug.value) return;
      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) {
        return;
      }

      deletingId.value = page.id;
      try {
        await api.pageService.deleteCmsPage(agencySlug.value, page.id);
        pages.value = pages.value.filter((item) => item.id !== page.id);
        toast.success(t('cms.pages.deleteSuccess'));
      } catch (error: any) {
        console.error('Failed to delete page', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        deletingId.value = null;
      }
    };

    const getImageStyle = (page: CmsPageCard) => {      
      if (page.featuredImage) {
        return {
          backgroundImage: `url('${page.featuredImage}')`,
        };
      }
      return {
        background: 'linear-gradient(135deg, #000000 0%, #2D2D2D 100%)',
      };
    };

    const truncateHtml = (html: string, maxLength = 150): string => {
      if (!html) return '';
      
      // Create a temporary div to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Get text content to check length
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      
      // If text is short enough, return original HTML
      if (textContent.length <= maxLength) {
        return html;
      }
      
      // Otherwise, truncate by walking through text nodes
      let currentLength = 0;
      const walker = document.createTreeWalker(
        tempDiv,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let node;
      while ((node = walker.nextNode())) {
        const nodeLength = node.textContent?.length || 0;
        if (currentLength + nodeLength > maxLength) {
          const remaining = maxLength - currentLength;
          if (node.textContent) {
            node.textContent = node.textContent.substring(0, remaining) + '...';
          }
          // Remove all following nodes
          let nextNode;
          while ((nextNode = walker.nextNode())) {
            if (nextNode.parentNode) {
              nextNode.parentNode.removeChild(nextNode);
            }
          }
          break;
        }
        currentLength += nodeLength;
      }
      
      return tempDiv.innerHTML;
    };

    return {
      pages,
      filteredPages,
      selectedStatus,
      selectedPageType,
      onlyPublic,
      searchQuery,
      statusOptions,
      pageTypeOptions,
      isLoading,
      errorMessage,
      publishingId,
      deletingId,
      hoveredPageId,
      loadPages,
      navigateToCreate,
      navigateToEdit,
      togglePublish,
      deletePage,
      paginationMeta,
      handlePageChange,
      handlePerPageChange,
      getImageStyle,
      truncateHtml,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-pages {
  color: $cms-text-primary;
}

.cms-toggle-group {
  display: flex;
  gap: $cms-spacing-sm;
  flex-wrap: wrap;
}

.cms-pages-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $cms-spacing-xxl 0;
  gap: $cms-spacing-sm;
  color: $cms-text-secondary;
}

.cms-loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: $cms-primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.cms-pages-error {
  display: flex;
  align-items: center;
  gap: $cms-spacing-md;
  padding: $cms-spacing-md;
  margin-bottom: $cms-spacing-md;
  background: $cms-danger-light;
  border: 1px solid $cms-danger-border;
  border-radius: $cms-radius-md;
  color: $cms-danger-color;
}

.cms-btn-link {
  background: none;
  border: none;
  padding: 0;
  color: $cms-primary-color;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}

.cms-page-card-wrapper {
  width: 100%;
  margin-bottom: 1rem;
}

.cms-page-card {
  display: flex;
  flex-direction: column;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease-in-out;
  background: white;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.05);
  height: 100%;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    transform: translateY(-4px);
    border-color: rgba(238, 42, 123, 0.2);
  }
}

// Image Section
.cms-page-image-section {
  position: relative;
  width: 100%;
  height: 220px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%);
    z-index: 1;
  }
}

.cms-page-badges {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  right: 0.75rem;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  z-index: 5;
  pointer-events: none;
}

.cms-page-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  color: white;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &.published {
    background: rgba(76, 175, 80, 0.8);
    border-color: rgba(76, 175, 80, 1);
  }

  &.draft {
    background: rgba(255, 152, 0, 0.8);
    border-color: rgba(255, 152, 0, 1);
  }
}

.cms-page-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  color: white;
  background: rgba(238, 42, 123, 0.8);
  border: 1px solid rgba(238, 42, 123, 1);
}

.cms-page-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 0.75rem;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;
  z-index: 10;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
}

.cms-page-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  align-items: flex-end;
  position: relative;
  z-index: 20;
}

.cms-page-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 0.25rem;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
  z-index: 30;
  pointer-events: auto;

  &:hover {
    background: white;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.edit-btn {
    color: #2196F3;

    &:hover:not(:disabled) {
      background: rgba(33, 150, 243, 0.1);
    }
  }

  &.delete-btn {
    color: #f44336;

    &:hover:not(:disabled) {
      background: rgba(244, 67, 54, 0.1);
    }
  }

  &.publish-btn {
    color: #4CAF50;

    &:hover:not(:disabled) {
      background: rgba(76, 175, 80, 0.1);
    }
  }
}

.cms-page-no-image {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  z-index: 1;
}

.cms-page-no-image-text {
  font-size: 0.75rem;
  font-weight: 500;
}

// Content Section
.cms-page-content {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
}

.cms-page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
}

.cms-page-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: #333;
  line-height: 1.4;
  flex: 1;
}

.cms-page-excerpt {
  color: #666;
  margin: 0;
  line-height: 1.5;
  font-size: 0.875rem;
  
  // Style HTML content inside
  :deep(p) {
    margin: 0 0 0.5rem 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  :deep(strong), :deep(b) {
    font-weight: 600;
    color: #333;
  }
  
  :deep(em), :deep(i) {
    font-style: italic;
  }
  
  :deep(ul), :deep(ol) {
    margin: 0.5rem 0;
    padding-left: 1.25rem;
  }
  
  :deep(li) {
    margin: 0.25rem 0;
  }
}

.cms-page-meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cms-page-meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #666;

  span {
    color: #333;
  }
}

.cms-page-footer {
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.cms-page-locale {
  font-size: 0.75rem;
  font-weight: 600;
  color: $cms-primary-color;
  padding: 0.25rem 0.5rem;
  background: rgba(238, 42, 123, 0.1);
  border-radius: 4px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
